import { FlightIcon } from '@/assets/icons'
import { countLabel, pluralize } from '@/utils/plural'
import { PublicSectionGate } from '@components/PublicPage'
import { publicSectionPath } from '@components/TripOverview/core'
import { usePublicFlights, usePublicOverview } from '@hooks'
import { normalizeExternalUrl } from '@lib/utils'
import {
	FlightResponse,
	GetPublicFlightsResponse,
	GetPublicOverviewResponse,
} from '@orbit/api-types'
import React from 'react'
import { useParams } from 'react-router-dom'
import {
	getArrivalDisplay,
	getDepartureDisplay,
	getDurationString,
	getFlightCode,
	getLayoverString,
} from '../Flights/flightHelpers'
import {
	AvatarStack,
	DetailBody,
	DetailCard,
	LayoverBanner,
	LegCard,
	LegCode,
	LegMeta,
	LegPassengerRow,
	LegRouteAirport,
	LegRouteArrow,
	LegRouteBlock,
	LegRouteEndpoint,
	LegsContainer,
	LegTimeBlock,
	LegTimeLabel,
	LegTimeValue,
	LinkItem,
	LinksList,
	MetaSection,
	NotesContent,
	NotFoundMessage,
	StackedAvatar,
	PassengerCountText,
	SectionLabel,
} from '../Flights/FlightDetailPage/FlightDetailPage.styles'
import { buildTravelerLookup, travelerFullName } from './publicMembers'
import PublicSectionLayout from './PublicSectionLayout'
import { iconSizes } from '@/theme/tokens'

const MAX_VISIBLE_AVATARS = 5

/** What the page renders from: the flights list plus the travelers that name passengers. */
interface FlightDetailData {
	detail: GetPublicFlightsResponse
	travelers: GetPublicOverviewResponse['travelers']
}

const getLegPassengerIds = (leg: FlightResponse): number[] => leg.passengers.map(p => p.userId)

/**
 * Public, read-only flight detail at
 * `/shared/trips/:tripCode/flights/:flightGroupId` — the member detail's legs
 * (route, times, layovers, duration, passenger avatars), links, and notes,
 * minus every edit affordance (no edit/delete, no add-leg callout, no leg
 * click-through) and minus attachments (not publicly resolvable — ADR 0003).
 * The group resolves client-side from the already-cached public flights list,
 * exactly how the member page resolves from its list query; passenger
 * names/avatars resolve from the overview's `travelers`. Both queries are
 * load-bearing, so the gate runs on their combined state, the flights error
 * (the deterministic 404 when the section is OFF) taking precedence.
 */
const PublicFlightDetailPage: React.FC = () => {
	const { tripCode, flightGroupId } = useParams<{ tripCode: string; flightGroupId: string }>()
	const flightsQuery = usePublicFlights(tripCode)
	const overviewQuery = usePublicOverview(tripCode)

	const combined = {
		data:
			flightsQuery.data && overviewQuery.data
				? ({ detail: flightsQuery.data, travelers: overviewQuery.data.travelers } satisfies FlightDetailData)
				: undefined,
		isLoading: flightsQuery.isLoading || overviewQuery.isLoading,
		error: flightsQuery.error ?? overviewQuery.error,
	}

	const listParent = { label: 'Flights', path: publicSectionPath(tripCode ?? '', 'flights') }

	return (
		<PublicSectionGate
			query={combined}
			loadingLabel="Loading flight…"
			unavailableTitle="Flight Unavailable"
			unavailableMessage="This flight doesn't exist or hasn't been shared publicly by the trip owner."
		>
			{({ detail, travelers }) => {
				const group = detail.flightGroups.find(g => String(g.id) === flightGroupId)
				const lookup = buildTravelerLookup(travelers)

				if (!group) {
					return (
						<PublicSectionLayout
							tripCode={tripCode ?? ''}
							title="Flights"
							backTo={listParent}
						>
							<NotFoundMessage>Flight not found</NotFoundMessage>
						</PublicSectionLayout>
					)
				}

				const firstLeg = group.flights[0]
				const links = group.links ?? []
				const addedBy = travelerFullName(lookup(group.createdByUserId))

				const passengerCount = new Set(group.flights.flatMap(getLegPassengerIds)).size
				const legCount = group.flights.length
				const subtitle = `${countLabel(legCount, 'leg')} · ${countLabel(passengerCount, 'passenger')}`

				return (
					<PublicSectionLayout
						tripCode={tripCode ?? ''}
						title={firstLeg ? getFlightCode(firstLeg) : 'Flight'}
						subtitle={subtitle}
						backTo={listParent}
					>
						<DetailCard>
							<DetailBody>
								<div>
									<SectionLabel>Legs ({legCount})</SectionLabel>
									<LegsContainer>
										{group.flights.map((leg, index) => {
											const prevLeg = index > 0 ? group.flights[index - 1] : null
											const layover = prevLeg
												? getLayoverString(prevLeg.arrivalTime, leg.departureTime)
												: null
											const depStr = getDepartureDisplay(leg)
											const arrStr = getArrivalDisplay(leg)
											const depCode =
												leg.departureAirportCode ?? leg.departureAirport?.iataCode ?? 'N/A'
											const arrCode =
												leg.arrivalAirportCode ?? leg.arrivalAirport?.iataCode ?? 'N/A'
											const passengerIds = getLegPassengerIds(leg)
											const visibleIds = passengerIds.slice(0, MAX_VISIBLE_AVATARS)
											const overflowCount = passengerIds.length - MAX_VISIBLE_AVATARS
											return (
												<React.Fragment key={leg.id}>
													{layover && <LayoverBanner>{layover}</LayoverBanner>}
													<LegCard $isManual={leg.isManuallyAdded} $interactive={false}>
														<LegCode>{getFlightCode(leg)}</LegCode>

														<LegRouteBlock>
															<LegRouteEndpoint $align="start">
																<LegRouteAirport>{depCode}</LegRouteAirport>
																<LegTimeBlock>
																	<LegTimeLabel>Departs</LegTimeLabel>
																	<LegTimeValue>{depStr ?? 'N/A'}</LegTimeValue>
																</LegTimeBlock>
															</LegRouteEndpoint>
															<LegRouteArrow>
																<FlightIcon sx={{ fontSize: iconSizes.lg }} />
															</LegRouteArrow>
															<LegRouteEndpoint $align="end">
																<LegRouteAirport>{arrCode}</LegRouteAirport>
																<LegTimeBlock>
																	<LegTimeLabel>Arrives</LegTimeLabel>
																	<LegTimeValue>{arrStr ?? 'N/A'}</LegTimeValue>
																</LegTimeBlock>
															</LegRouteEndpoint>
														</LegRouteBlock>

														{leg.durationMinutes != null && (
															<LegMeta>
																<span>Duration · {getDurationString(leg.durationMinutes)}</span>
															</LegMeta>
														)}

														<LegPassengerRow>
															<AvatarStack>
																{visibleIds.map((userId, idx) => {
																	const traveler = lookup(userId)
																	if (!traveler) return null
																	return (
																		<StackedAvatar key={userId} $index={idx} user={traveler} size="sm" ring />
																	)
																})}
																{overflowCount > 0 && (
																	<StackedAvatar $index={MAX_VISIBLE_AVATARS} size="sm" ring tone="gold" label={`+${overflowCount}`} />
																)}
															</AvatarStack>
															<PassengerCountText>
																{passengerIds.length}{' '}
																{pluralize(passengerIds.length, 'passenger')}
															</PassengerCountText>
														</LegPassengerRow>
													</LegCard>
												</React.Fragment>
											)
										})}
									</LegsContainer>
								</div>

								{links.length > 0 && (
									<div>
										<SectionLabel>Links ({links.length})</SectionLabel>
										<LinksList>
											{links.map((link, index) => (
												<LinkItem
													key={index}
													href={normalizeExternalUrl(link)}
													target="_blank"
													rel="noopener noreferrer"
												>
													{link}
												</LinkItem>
											))}
										</LinksList>
									</div>
								)}

								{group.notes && (
									<div>
										<SectionLabel>Notes</SectionLabel>
										<NotesContent>{group.notes}</NotesContent>
									</div>
								)}

								{addedBy && (
									<MetaSection>
										<span>Added by {addedBy}</span>
									</MetaSection>
								)}
							</DetailBody>
						</DetailCard>
					</PublicSectionLayout>
				)
			}}
		</PublicSectionGate>
	)
}

export default PublicFlightDetailPage
